import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { supabase } from '@lib/supabase'
import { CheckCircle2, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'

const AuthCallback = () => {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [status, setStatus] = useState('loading') // loading, success, error
  const [message, setMessage] = useState('Authenticating...')

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const searchParams = new URLSearchParams(window.location.search)
        const type = hashParams.get('type')
        const accessToken = hashParams.get('access_token')
        const oauthCode = searchParams.get('code')

        console.log('[AuthCallback] Processing callback:', { type, hasToken: !!accessToken, hasOAuthCode: !!oauthCode })

        // If there's a PKCE code in the URL, exchange it explicitly
        if (oauthCode) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href)
          if (exchangeError) {
            console.error('[AuthCallback] Code exchange error:', exchangeError)
            throw exchangeError
          }
        }

        // Wait for the auth state to settle
        const session = await new Promise((resolve) => {
          const timeout = setTimeout(async () => {
            const { data: { session: s } } = await supabase.auth.getSession()
            resolve(s)
          }, 2000)

          const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              clearTimeout(timeout)
              subscription.unsubscribe()
              resolve(s)
            }
          })
        })

        if (!session) {
          throw new Error('No active session found. Please try logging in again.')
        }

        // Check if session exists (which means auth was successful)
        if (session) {
          // Detect Google OAuth: code in query params (PKCE) or provider in app_metadata
          const provider = session.user.app_metadata?.provider
          const providers = session.user.app_metadata?.providers || []
          const isGoogleOAuth = provider === 'google' || providers.includes('google')

          if (isGoogleOAuth) {
            console.log('[AuthCallback] Google OAuth callback detected')
            setMessage('Checking your account...')

            // Check if this user has completed the consent gate
            const { data: userRecord, error: userError } = await supabase
              .from('users')
              .select('sms_transactional_consent, role, phone')
              .eq('id', session.user.id)
              .maybeSingle()

            if (userError) {
              console.error('[AuthCallback] Error fetching user record:', userError)
            }

            // Phone is null until the user completes the consent gate form.
            // sms_transactional_consent defaults to false in the DB so we cannot
            // use it alone to detect new vs returning users.
            const hasCompletedConsent = userRecord &&
              userRecord.phone !== null &&
              userRecord.phone !== undefined

            if (!hasCompletedConsent) {
              // New Google user — must complete consent before accessing the app
              console.log('[AuthCallback] New Google user, redirecting to consent gate')
              setStatus('success')
              setMessage('Almost there! Completing your account setup...')
              setTimeout(() => navigate('/complete-signup', { replace: true }), 500)
            } else {
              // Existing Google user — welcome back, send to dashboard
              console.log('[AuthCallback] Existing Google user, redirecting to dashboard')
              setStatus('success')
              setMessage('Signed in successfully!')
              toast.success('Welcome back!')
              setTimeout(() => {
                if (userRecord.role === 'photographer') {
                  navigate('/talent/dashboard', { replace: true })
                } else if (userRecord.role === 'admin') {
                  navigate('/lp-console', { replace: true })
                } else {
                  navigate('/dashboard', { replace: true })
                }
              }, 1000)
            }
          } else if (type === 'recovery') {
            // Password recovery - this shouldn't happen here, but handle it
            console.log('[AuthCallback] Password recovery callback')
            setStatus('success')
            setMessage('Password reset session established')
            toast.success('You can now reset your password')
            setTimeout(() => navigate('/reset-password'), 1500)
          } else if (type === 'signup') {
            // Email confirmation after signup
            console.log('[AuthCallback] Signup confirmation callback')
            setStatus('success')
            setMessage('Email confirmed successfully!')
            toast.success('Email confirmed! Welcome!')
            setTimeout(() => navigate('/'), 1500)
          } else if (type === 'email_change') {
            // Email change confirmation
            console.log('[AuthCallback] Email change confirmation callback')
            setStatus('success')
            setMessage('Email updated successfully!')
            toast.success('Your email has been updated')

            // Redirect based on user role
            setTimeout(() => {
              if (profile?.role === 'photographer') {
                navigate('/talent/dashboard/settings')
              } else {
                navigate('/customer/profile')
              }
            }, 1500)
          } else {
            // Generic callback or type not specified
            console.log('[AuthCallback] Generic auth callback')
            setStatus('success')

            if (profile) {
              setMessage('Email updated successfully!')
              toast.success('Your email has been updated')

              setTimeout(() => {
                if (profile.role === 'photographer') {
                  navigate('/talent/dashboard/settings')
                } else {
                  navigate('/customer/profile')
                }
              }, 1500)
            } else {
              setMessage('Authentication successful!')

              setTimeout(() => {
                if (profile?.role === 'photographer') {
                  navigate('/talent/dashboard')
                } else if (profile?.role === 'admin') {
                  navigate('/lp-console')
                } else {
                  navigate('/customer/dashboard')
                }
              }, 1500)
            }
          }
        } else {
          throw new Error('Authentication could not be verified')
        }
      } catch (error) {
        console.error('[AuthCallback] Error:', error)
        setStatus('error')
        setMessage(error.message || 'Authentication failed')
        toast.error('Authentication failed. Please try again.')
        setTimeout(() => navigate('/login'), 3000)
      }
    }

    handleAuthCallback()
  }, [navigate, profile])

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-sage-50 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-dusty-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Success!
            </h2>
            <p className="text-dusty-600">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Redirecting you...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Authentication Failed
            </h2>
            <p className="text-dusty-600">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Redirecting to login...
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default AuthCallback
